import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function StaffUpdatedCSV() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // CSV headers - All updatable columns from core_mstr_staff
  const csvHeaders = [
    "staff_code",
    "salutation",
    "staff_name",
    "aadhar_no",
    "dob",
    "doj",
    "dor",
    "gender",
    "qualification",
    "designation",
    "staff_type",
    "mobile",
    "email",
    "staff_status",
  ];

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "staff_update_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "staff_code", headerName: "Staff Code", width: 150 },
    { field: "salutation", headerName: "Salutation", width: 100 },
    { field: "staff_name", headerName: "Staff Name", width: 200 },
    { field: "aadhar_no", headerName: "Aadhar No", width: 150 },
    { field: "dob", headerName: "Date of Birth", width: 130 },
    { field: "doj", headerName: "Date of Joining", width: 130 },
    { field: "dor", headerName: "Date of Retirement", width: 130 },
    { field: "gender", headerName: "Gender", width: 100 },
    { field: "qualification", headerName: "Qualification", width: 200 },
    { field: "designation", headerName: "Designation", width: 150 },
    { field: "staff_type", headerName: "Staff Type", width: 100 },
    { field: "mobile", headerName: "Mobile", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "staff_status", headerName: "Staff Status", width: 150 },
    {
      field: "errors",
      headerName: "Errors",
      width: 300,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  // Parse + validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];

          // Mandatory field validation
          if (!row.staff_code) {
            errors.push("Missing staff_code");
          }

          // Email validation
          if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push("Invalid email format");
          }

          // Mobile validation (10 digits)
          if (row.mobile && !/^\d{10}$/.test(row.mobile)) {
            errors.push("Mobile must be 10 digits");
          }

          // Aadhar validation (12 digits)
          if (row.aadhar_no && !/^\d{12}$/.test(row.aadhar_no)) {
            errors.push("Aadhar must be 12 digits");
          }

          // Date format validation (YYYY-MM-DD)
          if (row.dob && !/^\d{4}-\d{2}-\d{2}$/.test(row.dob)) {
            errors.push("DOB must be YYYY-MM-DD");
          }

          if (row.doj && !/^\d{4}-\d{2}-\d{2}$/.test(row.doj)) {
            errors.push("DOJ must be YYYY-MM-DD");
          }

          if (row.dor && !/^\d{4}-\d{2}-\d{2}$/.test(row.dor)) {
            errors.push("DOR must be YYYY-MM-DD");
          }

          // Gender validation
          if (row.gender && !["M", "F", "O"].includes(row.gender)) {
            errors.push("Gender must be M, F, or O");
          }

          return {
            id: index + 1,
            sno: index + 1,
            ...row,
            errors,
          };
        });
        setPreviewData(validatedData);
      },
    });
  };

  // Confirm upload
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        alert("No valid rows to upload!");
        return;
      }

      const res = await axios.post(
        `${API_BASE_URL}/api/staff/update-from-csv/${clientId}/${collegeId}/data`,
        { staff_records: validRows }
      );

      alert(res.data.message || "Staff updated successfully");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      console.error(err);
      alert("Error uploading data: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Download Template Button */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{
          backgroundColor: "#388e3c",
          color: "#fff",
          "&:hover": { backgroundColor: "#2c6b2f" },
        }}
      >
        Download CSV
      </Button>

      {/* File upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            ml: 2,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>

      {/* DataGrid preview */}
      {previewData.length > 0 && (
        <>
          <Box sx={{ mt: 3, height: 500 }}>
            <DataGrid
              rows={previewData}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              disableRowSelectionOnClick
            />
          </Box>
          <Button
            sx={{
              mt: 2,
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
            }}
            variant="contained"
            onClick={handleConfirm}
          >
            Confirm & Upload
          </Button>
        </>
      )}
    </Box>
  );
}
